"use client"

import { useState, useRef, useCallback, useEffect, useMemo, memo } from "react"
import { useSettingsStore } from "@/lib/settings-store"
import { useGridVideoThumb } from "@/hooks/use-grid-video-thumb"
import Image from "next/image"
import { Heart, Layers, FileVideo, Glasses, SquaresFour, Gem, Play, Square, ScrollText, Pause, Loader2, ImageOff } from "@/lib/icons"
import { getThumbnailFallbacks } from "@/lib/image-utils"
import { useForcePreview } from "@/hooks/use-force-preview"
import { useAlbumHoverPreview } from "@/hooks/use-album-hover-preview"
import type { BooruPost } from "@/lib/booru-api"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"
import { useGridQuickPlay } from "@/hooks/use-grid-quick-play"
import { inferOrderMediaType } from "@/lib/order-media"
import type { TopScript } from "@/hooks/use-post-script-counts"

/**
 * Natural dimensions learned from images that had none in their post data,
 * keyed by the image URL. Module-level so it survives unmount/remount and
 * is shared by every card.
 *
 * Why this exists: masonry's no-known-dimensions path renders a bare <img>
 * with no width/height, which reserves ZERO height until the image
 * actually decodes — so each card sits collapsed and then snaps to full
 * size on load, shoving everything below it down the column. That is the
 * residual "content jumps while scrolling" left over after the masonry
 * column-assignment fixes (those stopped items moving BETWEEN columns;
 * measured live, `left` never changes any more, but vertical positions
 * still shifted as images decoded). Remembering a size the first time we
 * see it means every later render of that image reserves the right box up
 * front instead of collapsing again.
 */
const learnedNaturalSizes = new Map<string, { w: number; h: number }>()

// Remember learned sizes across visits too (not just within one page load): a returning visitor then
// gets the exact box for every thumbnail they've already seen, so those cards never shift at all.
const LEARNED_SIZES_KEY = "goonru_thumb_sizes_v1"
const LEARNED_SIZES_MAX = 1500
if (typeof window !== "undefined") {
  try {
    const raw = window.localStorage.getItem(LEARNED_SIZES_KEY)
    if (raw) for (const [k, w, h] of JSON.parse(raw) as [string, number, number][]) learnedNaturalSizes.set(k, { w, h })
  } catch {
    // storage unavailable or corrupt: just start empty
  }
}
let persistTimer: ReturnType<typeof setTimeout> | null = null
function rememberNaturalSize(key: string, w: number, h: number) {
  if (!key || !w || !h || learnedNaturalSizes.has(key)) return false
  learnedNaturalSizes.set(key, { w, h })
  if (typeof window !== "undefined" && !persistTimer) {
    persistTimer = setTimeout(() => {
      persistTimer = null
      try {
        const entries = [...learnedNaturalSizes.entries()].slice(-LEARNED_SIZES_MAX).map(([k, v]) => [k, v.w, v.h])
        window.localStorage.setItem(LEARNED_SIZES_KEY, JSON.stringify(entries))
      } catch {
        // quota or private mode: fine, it is only a cache
      }
    }, 2000)
  }
  return true
}

/** Placeholder shape before anything is known — portrait-ish, matching the
 *  typical card, so a first-load reservation is close rather than zero. */
const FALLBACK_ASPECT = "3 / 4"

function formatDurationBadge(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`
}

interface BooruPostCardProps {
  post: BooruPost
  imageSrc: string
  // When provided, enables desktop hover-scrub and mobile tap-to-preview
  scrubVideoSrc?: string
  borderRadius: number
  isFav: boolean
  isVideoPost: boolean
  isVRVideo: boolean
  hasGifTag?: boolean
  postIsAlbum: boolean
  albumPageCount: number
  showPremiumBadge: boolean
  scriptCount: number
  /** Most-popular attached script, when free — see hooks/use-grid-quick-play.ts.
   * Undefined (or a paid script) means no quick-play button renders. */
  topScript?: TopScript
  onClick: () => void
  onFavorite: (e: React.MouseEvent) => void
  onQueueGooncave: (e: React.MouseEvent) => void
  /** Draws attention to the "+" as the intended action while the user is picking
   *  content for a specific Gooncave slot. */
  highlightQueueGooncave?: boolean
  onMouseDown: () => void
  onMouseUp: () => void
  // Called on mouse-leave in addition to internal scrub cleanup
  onMouseLeaveOuter: () => void
  onPremiumClick: (e: React.MouseEvent) => void
  // IntersectionObserver callback for Doujins metadata prefetch
  observeRef?: (el: HTMLDivElement | null) => void
  /** Masonry mode only — when a post has no real width/height, size the
   * thumbnail from the actual loaded image instead of a fixed 300x300
   * fallback. Square/grid mode leaves this off on purpose: a uniform
   * square IS the intended look there for posts with no known dimensions. */
  useNaturalSizeFallback?: boolean
  /** Fitted layout: the card is a fixed-shape cell and the image is fitted into it. */
  fitted?: { ratio: string; fit: "contain" | "cover" | "fill"; blur: boolean }
}

export const BooruPostCard = memo(function BooruPostCard({
  post,
  imageSrc,
  scrubVideoSrc,
  borderRadius,
  isFav,
  isVideoPost,
  isVRVideo,
  hasGifTag,
  postIsAlbum,
  useNaturalSizeFallback = false,
  fitted,
  albumPageCount,
  showPremiumBadge,
  scriptCount,
  topScript,
  onClick,
  onFavorite,
  onQueueGooncave,
  highlightQueueGooncave = false,
  onMouseDown,
  onMouseUp,
  onMouseLeaveOuter,
  onPremiumClick,
  observeRef,
}: BooruPostCardProps) {
  const t = useTranslations("booruPostCard")
  // A "direct" thumb is when the preview URL is itself a video file (rare — most boorus give image thumbs)
  const isDirectVideoThumb = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?|$)/i.test(imageSrc)

  // When the thumbnail fails to load, try the post's other pictures in turn (sample, full file); when every one has
  // failed, show an "unavailable" tile instead of a broken image.
  const thumbFallbacks = useMemo(
    () => (isDirectVideoThumb || !imageSrc ? [imageSrc] : getThumbnailFallbacks(imageSrc, post)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageSrc, isDirectVideoThumb, post.previewUrl, post.sampleUrl, post.fileUrl],
  )
  const [thumbAttempt, setThumbAttempt] = useState(0)
  useEffect(() => setThumbAttempt(0), [imageSrc])
  const shownSrc = thumbFallbacks[thumbAttempt]
  const thumbUnavailable = !isDirectVideoThumb && !!imageSrc && shownSrc === undefined
  const handleThumbError = useCallback(() => setThumbAttempt((n) => n + 1), [])

  // Bumped once when this image's real dimensions are first learned, purely
  // to re-render with the exact aspect ratio — see learnedNaturalSizes.
  const [, setLearnedAspectTick] = useState(0)
  const learnedSize = imageSrc ? learnedNaturalSizes.get(imageSrc) : undefined
  // Fitted layout: the card box has a fixed ratio and every thumbnail fills it (object-fit decides how).
  const thumbCls = fitted ? "absolute inset-0 w-full h-full" : "w-full h-auto block"
  const fitStyle = fitted ? ({ objectFit: fitted.fit, aspectRatio: undefined, maxHeight: undefined } as React.CSSProperties) : undefined
  const reservedAspect = learnedSize ? `${learnedSize.w} / ${learnedSize.h}` : FALLBACK_ASPECT

  // Scrub is available when we have a video post with a static image thumb + a separate video URL
  const canScrub = isVideoPost && !isDirectVideoThumb && !!scrubVideoSrc

  // Free scripts only — see hooks/use-grid-quick-play.ts for why paid ones don't get a button.
  const freeTopScript = topScript && topScript.priceCents === 0 ? topScript : undefined
  const quickPlay = useGridQuickPlay(post, freeTopScript)
  // A script does NOT need media to play. The engine runs on its own
  // performance.now() clock (lib/script-playback-engine.ts) and only needs
  // actions; syncing to a media element is an enhancement layered on top,
  // not a requirement. So the button shows for ANY content with a free
  // script — image, video or audio — and the two srcs below only decide
  // what, if anything, plays alongside the device.
  const quickPlayVideoSrc = isVideoPost ? scrubVideoSrc || (isDirectVideoThumb ? imageSrc : undefined) : undefined
  const quickPlayAudioSrc =
    !quickPlayVideoSrc && post.fileUrl && inferOrderMediaType(post.fileUrl, post.type) === "audio"
      ? post.fileUrl
      : undefined
  const canQuickPlay = !!freeTopScript

  // --- Desktop hover-scrub state ---
  const [scrubActive, setScrubActive] = useState(false)
  const [videoReady, setVideoReady] = useState(false)
  const scrubTimerRef = useRef<NodeJS.Timeout | null>(null)
  const scrubVideoRef = useRef<HTMLVideoElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const thumbVideoRef = useRef<HTMLVideoElement>(null)

  // Video-file thumbnails: lazy + capped (see hooks/use-grid-video-thumb.ts); the user's
  // "Grid video previews" setting decides hover-only, always-looping or never.
  const gridVideoPreviews = useSettingsStore((s) => s.gridVideoPreviews)
  const thumb = useGridVideoThumb(cardRef, isDirectVideoThumb && gridVideoPreviews !== "off")
  useEffect(() => {
    const v = thumbVideoRef.current
    if (!v || gridVideoPreviews !== "always" || quickPlay.isActive) return
    if (thumb.load && thumb.inView) v.play().catch(() => {})
    else v.pause()
  }, [gridVideoPreviews, thumb.load, thumb.inView, quickPlay.isActive])

  // --- Mobile tap-to-preview state ---
  const [mobilePlaying, setMobilePlaying] = useState(false)
  const mobileVideoRef = useRef<HTMLVideoElement>(null)

  // Force preview: with the setting on, the video post nearest the middle of the screen plays a muted looping preview, one at a
  // time. Hover scrub, the mobile tap preview and device quick-play all take priority over it (see hooks/use-force-preview.ts).
  const forcePreviewWinner = useForcePreview(`${post.site}-${post.id}`, cardRef, canScrub && !quickPlay.isActive)
  const showForcedPreview = forcePreviewWinner && !scrubActive && !mobilePlaying

  // Album hover preview (setting): resting the pointer on a multi-picture card flips through its pictures.
  const albumPreview = useAlbumHoverPreview(post)

  // Desktop: start scrub after 300ms hover delay
  const handleMouseEnter = () => {
    albumPreview.start()
    if (!canScrub || quickPlay.isActive) return
    scrubTimerRef.current = setTimeout(() => setScrubActive(true), 300)
  }

  // Desktop: scrub position = mouse X fraction of card width
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!scrubActive || quickPlay.isActive || !scrubVideoRef.current || !videoReady || !cardRef.current) return
      const rect = cardRef.current.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const dur = scrubVideoRef.current.duration
      if (dur && isFinite(dur)) scrubVideoRef.current.currentTime = ratio * dur
    },
    [scrubActive, videoReady],
  )

  const handleMouseLeave = () => {
    if (scrubTimerRef.current) {
      clearTimeout(scrubTimerRef.current)
      scrubTimerRef.current = null
    }
    setScrubActive(false)
    setVideoReady(false)
    albumPreview.stop()
    onMouseLeaveOuter()
  }

  // Mobile: toggle muted inline preview
  const toggleMobilePreview = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMobilePlaying((p) => !p)
  }

  useEffect(() => {
    const vid = mobileVideoRef.current
    if (!vid) return
    if (mobilePlaying) {
      vid.play().catch(() => {})
    } else {
      vid.pause()
      vid.currentTime = 0
    }
  }, [mobilePlaying])

  return (
    <div
      ref={(el) => {
        ;(cardRef as React.MutableRefObject<HTMLDivElement | null>).current = el
        observeRef?.(el)
      }}
      className="group relative overflow-hidden cursor-pointer transition-transform hover:scale-[1.02]"
      style={{ borderRadius: `${borderRadius}px`, ...(fitted ? { aspectRatio: fitted.ratio, backgroundColor: "hsl(var(--muted))" } : {}) }}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      onMouseMove={canScrub ? handleMouseMove : undefined}
    >
      {/* Fitted + Fit: a blurred copy of the image fills the bars instead of empty space */}
      {fitted?.blur && fitted.fit === "contain" && !isDirectVideoThumb && shownSrc && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={shownSrc}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-125 object-cover opacity-70 blur-xl pointer-events-none"
          loading="lazy"
        />
      )}
      {/* ── Thumbnail ── */}
      {isDirectVideoThumb ? (
        gridVideoPreviews === "off" ? (
          // Previews off: a plain tile at the right shape, nothing is fetched.
          <div
            className={fitted ? "absolute inset-0 w-full h-full bg-muted" : "w-full bg-muted"}
            style={fitted ? { borderRadius: `${borderRadius}px` } : { aspectRatio: post.width && post.height ? `${post.width} / ${post.height}` : reservedAspect, borderRadius: `${borderRadius}px` }}
          />
        ) : (
          <video
            ref={thumbVideoRef}
            // No src until the card is near the screen AND a load slot is free.
            src={thumb.load ? imageSrc : undefined}
            {...(useNaturalSizeFallback && !(post.width && post.width > 0)
              ? {}
              : { width: post.width || 300, height: post.height || 300 })}
            className={thumbCls}
            style={{
              borderRadius: `${borderRadius}px`,
              // Always reserve a box. Without one the card sat collapsed and snapped open when the video's
              // metadata arrived (the biggest layout shift measured on the Erome grid).
              aspectRatio: post.width && post.height ? `${post.width} / ${post.height}` : reservedAspect,
              transition: "aspect-ratio 220ms ease-out",
              ...fitStyle,
            }}
            muted
            loop
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => {
              thumb.done()
              const v = e.currentTarget
              if (rememberNaturalSize(imageSrc || "", v.videoWidth, v.videoHeight)) setLearnedAspectTick((n) => n + 1)
            }}
            onError={thumb.done}
            // play() rejects with AbortError when the mouse leaves (pause) before it resolves — expected, not an error.
            onMouseEnter={(e) => { if (!quickPlay.isActive) e.currentTarget.play().catch(() => {}) }}
            onMouseLeave={(e) => {
              if (quickPlay.isActive || gridVideoPreviews === "always") return
              e.currentTarget.pause()
              e.currentTarget.currentTime = 0
            }}
          />
        )
      ) : thumbUnavailable ? (
        <div
          className={cn("flex flex-col items-center justify-center gap-1 bg-muted text-muted-foreground", fitted ? "absolute inset-0 w-full h-full" : "w-full")}
          style={fitted ? { borderRadius: `${borderRadius}px` } : { aspectRatio: post.width && post.height ? `${post.width} / ${post.height}` : reservedAspect, borderRadius: `${borderRadius}px` }}
        >
          <ImageOff className="h-6 w-6 opacity-60" aria-hidden />
          <span className="text-xs">{t("imageUnavailable")}</span>
        </div>
      ) : post.width && post.width > 0 && post.height && post.height > 0 ? (
        // Real dimensions known (structured API sources) — next/image can
        // reserve the correct aspect ratio up front, no layout shift.
        <Image
          src={shownSrc || "/placeholder.svg"}
          onError={handleThumbError}
          alt={`Post ${post.id}`}
          width={post.width}
          height={post.height}
          className={thumbCls}
          style={{ borderRadius: `${borderRadius}px`, maxHeight: "800px", objectFit: "contain", ...fitStyle }}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          priority={false}
          loading="lazy"
          unoptimized
        />
      ) : useNaturalSizeFallback ? (
        // Masonry mode, no real dimensions available (most scraper-based
        // sources never had structured width/height to begin with). A
        // fixed 300x300 fallback here used to force every one of these
        // into a perfect square, which is what was flattening masonry mode
        // into a square grid — so the image still sizes itself from the
        // real thing rather than a forced square.
        //
        // But a bare <img> reserves NO height until it decodes, so the card
        // sits collapsed and then snaps open, pushing its whole column
        // down. `aspectRatio` reserves a correctly-shaped box up front
        // instead: the learned real ratio when this image has been seen
        // before (module cache above), otherwise a portrait-ish
        // placeholder that is at least close. onLoad records the true
        // ratio so every later render of this image is exact.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={shownSrc || "/placeholder.svg"}
          onError={handleThumbError}
          alt={`Post ${post.id}`}
          className={thumbCls}
          style={{
            borderRadius: `${borderRadius}px`,
            maxHeight: "800px",
            objectFit: "contain",
            aspectRatio: reservedAspect,
            transition: "aspect-ratio 220ms ease-out",
            ...fitStyle,
          }}
          onLoad={(e) => {
            const img = e.currentTarget
            if (!img.naturalWidth || !img.naturalHeight) return
            // Re-render so this card swaps its placeholder ratio for the real one.
            // Only fires once per image (rememberNaturalSize returns false after the first).
            if (rememberNaturalSize(imageSrc || "", img.naturalWidth, img.naturalHeight)) setLearnedAspectTick((n) => n + 1)
          }}
          loading="lazy"
        />
      ) : (
        // Square/grid mode — a uniform square is the intended look here
        // when a post has no known real dimensions.
        <Image
          src={shownSrc || "/placeholder.svg"}
          onError={handleThumbError}
          alt={`Post ${post.id}`}
          width={300}
          height={300}
          className={thumbCls}
          style={{ borderRadius: `${borderRadius}px`, maxHeight: "800px", objectFit: "contain", ...fitStyle }}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          priority={false}
          loading="lazy"
          unoptimized
        />
      )}

      {/* ── Desktop scrub overlay ── */}
      {canScrub && !quickPlay.isActive && scrubActive && (
        <video
          ref={scrubVideoRef}
          // Only set src once scrub is active so the browser doesn't preload all thumbnails
          src={scrubActive ? scrubVideoSrc : undefined}
          className={cn(
            "absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-200",
            scrubActive && videoReady ? "opacity-100" : "opacity-0",
          )}
          style={{ borderRadius: `${borderRadius}px` }}
          muted
          playsInline
          preload="auto"
          onLoadedMetadata={() => setVideoReady(true)}
        />
      )}

      {/* ── Force preview: muted looping preview on the card nearest the middle of the screen ── */}
      {canScrub && !quickPlay.isActive && showForcedPreview && (
        <video
          src={scrubVideoSrc}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ borderRadius: `${borderRadius}px` }}
          muted
          loop
          playsInline
          autoPlay
          preload="auto"
          aria-hidden
        />
      )}

      {/* ── Album hover preview: steps through the album's pictures while the pointer rests on the card ── */}
      {albumPreview.frame && (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={albumPreview.frame.src}
            alt=""
            aria-hidden
            data-testid="album-hover-frame"
            className="absolute inset-0 h-full w-full object-cover pointer-events-none"
            style={{ borderRadius: `${borderRadius}px` }}
          />
          <div className="absolute bottom-2 right-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-white pointer-events-none">
            {albumPreview.frame.n} / {albumPreview.frame.total}
          </div>
        </>
      )}

      {/* ── Mobile muted preview (plays on tap) ── */}
      {canScrub && !quickPlay.isActive && mobilePlaying && (
        <video
          ref={mobileVideoRef}
          src={scrubVideoSrc}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ borderRadius: `${borderRadius}px` }}
          muted
          loop
          playsInline
          autoPlay
        />
      )}

      {/* ── Mobile ▶ / ■ button — only visible on touch devices ── */}
      {canScrub && !quickPlay.isActive && (
        <button
          type="button"
          aria-label={mobilePlaying ? t("stopPreview") : t("playPreview")}
          className={cn(
            "absolute bottom-10 right-2 z-20 p-1.5 rounded-full transition-opacity",
            "opacity-0 [@media(hover:none)]:opacity-100",
            mobilePlaying ? "bg-primary/80" : "bg-black/60",
          )}
          onClick={toggleMobilePreview}
        >
          {mobilePlaying ? (
            <Square className="h-3.5 w-3.5 text-white fill-white" />
          ) : (
            <Play className="h-3.5 w-3.5 text-white fill-white" />
          )}
        </button>
      )}

      {/* ── Device quick-play media. Only rendered when there IS something to
           play alongside the script: video covers the thumbnail, audio sits
           as a control bar so the artwork stays visible, and an image-only
           post gets neither — the script still plays against the device on
           the engine's own clock. See hooks/use-grid-quick-play.ts. ── */}
      {quickPlay.isActive && quickPlayVideoSrc && (
        <video
          ref={quickPlay.registerMedia}
          src={quickPlayVideoSrc}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ borderRadius: `${borderRadius}px` }}
          playsInline
          controls
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {quickPlay.isActive && quickPlayAudioSrc && (
        <audio
          ref={quickPlay.registerMedia}
          src={quickPlayAudioSrc}
          className="absolute bottom-0 left-0 right-0 w-full z-20"
          controls
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {canQuickPlay && (
        // pointer-events-none on the positioning wrapper so it never blocks the
        // card's own onClick (open detail modal) outside the icon itself — only
        // the button below re-enables pointer events, in just its own h-11/w-11.
        <div
          className={cn(
            "absolute inset-0 z-10 flex items-center justify-center pointer-events-none transition-opacity",
            quickPlay.isActive
              ? "opacity-0 hover:opacity-100 [@media(hover:none)]:opacity-100"
              : "opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-70",
          )}
        >
          <button
            type="button"
            aria-label={quickPlay.isPlaying ? t("pauseDevicePlayback") : t("playWithDevice")}
            title={quickPlay.isPlaying ? t("pauseDevicePlayback") : t("playWithDevice")}
            className="pointer-events-auto flex items-center justify-center h-11 w-11 rounded-full bg-black/60 backdrop-blur-sm shadow-lg hover:bg-black/70"
            onClick={(e) => {
              e.stopPropagation()
              quickPlay.toggle()
            }}
          >
            {quickPlay.isLoading ? (
              <Loader2 className="h-5 w-5 text-white animate-spin" />
            ) : quickPlay.isPlaying ? (
              <Pause className="h-5 w-5 text-white fill-white" />
            ) : (
              <Play className="h-5 w-5 text-white fill-white ml-0.5" />
            )}
          </button>
        </div>
      )}

      {/* ── Album page count badge ── */}
      {/* albumPageCount === -1 means "known to be a gallery, exact count not fetched yet" */}
      {/* (e.g. lazy-loaded Erome placeholders) — show the icon without a misleading number. */}
      {postIsAlbum && !showPremiumBadge && (
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-purple-600/90 backdrop-blur-sm text-white px-2 py-1 rounded-md shadow-lg">
          <Layers className="h-3 w-3" />
          {albumPageCount > 0 && <span className="text-xs font-medium">{albumPageCount}</span>}
        </div>
      )}

      {/* ── Premium badge ── */}
      {showPremiumBadge && (
        <div
          className="absolute top-2 left-2 flex items-center gap-1.5 bg-gradient-to-r from-yellow-500 to-amber-600 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg shadow-lg cursor-pointer hover:from-yellow-400 hover:to-amber-500 transition-all z-10"
          onClick={onPremiumClick}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <Gem className="h-4 w-4" />
          <span className="text-sm font-bold">PREMIUM</span>
        </div>
      )}

      {/* ── Video / VR badge ── */}
      {isVideoPost && (
        <div
          className={cn(
            "absolute top-2 right-2 flex items-center gap-1 backdrop-blur-sm text-white px-2 py-1.5 rounded-md shadow-lg",
            isVRVideo ? "bg-indigo-600/90" : "bg-black/70",
          )}
        >
          {isVRVideo ? <Glasses className="h-3 w-3" /> : <FileVideo className="h-3 w-3" />}
          {isVRVideo && (
            <span className="text-[10px] font-semibold uppercase tracking-wide leading-tight">VR</span>
          )}
          {!isVRVideo && hasGifTag && (
            <span className="text-[10px] font-semibold uppercase tracking-wide leading-tight">GIF</span>
          )}
          {typeof post.durationSeconds === "number" && (
            <span className="text-[10px] font-semibold tabular-nums leading-tight">
              {formatDurationBadge(post.durationSeconds)}
            </span>
          )}
        </div>
      )}

      {/* ── Bottom-left action icons: favorite + queue to Gooncave.
           Desktop (hover-capable): hidden until hover, same as before.
           Touch: no hover state exists, so these default to a dimmed-but-
           visible baseline instead of staying invisible — same technique
           as the mobile scrub button above, just dimmed rather than opaque
           so it doesn't clutter the thumbnail. Each button carries its own
           translucent backdrop for legibility, so there's no need for a
           full-bleed gradient behind the whole card anymore. ── */}
      <div className="absolute bottom-2 left-2 flex items-center gap-1.5 opacity-0 [@media(hover:none)]:opacity-60 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 w-7 p-0 rounded-full text-white bg-black/40 backdrop-blur-sm hover:bg-white/20",
            isFav && "text-red-500",
          )}
          onClick={onFavorite}
        >
          <Heart className={cn("h-3.5 w-3.5", isFav && "fill-current")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-7 w-7 p-0 rounded-full text-white bg-black/40 backdrop-blur-sm hover:bg-white/20",
            highlightQueueGooncave &&
              "text-pink-300 bg-pink-500/30 hover:bg-pink-500/40 ring-2 ring-pink-500/60 animate-glow-breathe",
          )}
          onClick={onQueueGooncave}
          aria-label={t("queueToGooncave")}
          title={t("queueToGooncave")}
        >
          <SquaresFour className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* ── Persistent favorite heart (fades on hover) ── */}
      {isFav && (
        <div className={cn("absolute top-2 group-hover:opacity-0 transition-opacity", isVideoPost ? "right-10" : "right-2")}>
          <Heart className="h-4 w-4 text-red-500 fill-current drop-shadow-lg" />
        </div>
      )}

      {/* ── Scripts badge ── */}
      {scriptCount > 0 && (
        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-primary/90 backdrop-blur-sm text-primary-foreground px-1.5 py-0.5 rounded shadow-md">
          <ScrollText className="h-2.5 w-2.5" />
          <span className="text-[10px] font-semibold">{scriptCount}</span>
        </div>
      )}
    </div>
  )
})
